import React from 'react';
import MuiPagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { baseFontFamilyStyle } from '../../shared/styles';

// Geometry (px) of the small pagination items, kept in sync with the sx overrides below
const ITEM_MIN_WIDTH = 24;
const ITEM_PADDING_X = 8;
const ITEM_MARGIN_X = 2;
const NAV_BUTTON_COUNT = 4; // first, previous, next, last
const LABEL_GAP = 12;
const SAFETY_MARGIN = 8;
const MAX_SIBLING_COUNT = 3;

const LABEL_FONT = `12px ${baseFontFamilyStyle}`;
const ITEM_FONT = `14px ${baseFontFamilyStyle}`;

let measureContext = null;

// @desc Measures rendered text width, falling back to a rough per-character estimate
const measureTextWidth = (text, font) => {
	if (!measureContext) {
		measureContext = document.createElement('canvas').getContext('2d');
	}
	if (!measureContext) return text.length * 8;
	measureContext.font = font;
	return Math.ceil(measureContext.measureText(text).width);
};

const itemWidth = text => Math.max(ITEM_MIN_WIDTH, measureTextWidth(text, ITEM_FONT) + ITEM_PADDING_X) + ITEM_MARGIN_X;

// @desc Picks the most informative layout that fits the available width. The first/previous/next/last
// buttons are always kept, so the user can reach the last page at any task pane width.
const getPaginationLayout = (availableWidth, pageCount, label) => {
	const navWidth = NAV_BUTTON_COUNT * (ITEM_MIN_WIDTH + ITEM_MARGIN_X);
	// Every page slot is sized as the widest page number so the layout never grows while paging
	const pageSlotWidth = itemWidth(String(Math.max(pageCount, 1)));
	const labelWidth = measureTextWidth(label, LABEL_FONT) + LABEL_GAP;

	for (const showLabel of [true, false]) {
		for (let siblingCount = MAX_SIBLING_COUNT; siblingCount >= 0; siblingCount--) {
			// MUI renders at most boundary + ellipsis + siblings + current + siblings + ellipsis + boundary
			const pageSlots = Math.min(pageCount, 2 * siblingCount + 5);
			const requiredWidth = navWidth + pageSlots * pageSlotWidth + (showLabel ? labelWidth : 0) + SAFETY_MARGIN;
			if (requiredWidth <= availableWidth) {
				return { showLabel, siblingCount, boundaryCount: 1, currentPageOnly: false };
			}
		}
	}

	return { showLabel: false, siblingCount: 0, boundaryCount: 0, currentPageOnly: true };
};

// @desc Tracks the width of an element as the task pane is resized
const useElementWidth = ref => {
	const [width, setWidth] = React.useState(0);

	React.useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return undefined;

		const updateWidth = () => setWidth(element.clientWidth);
		updateWidth();

		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateWidth);
			return () => window.removeEventListener('resize', updateWidth);
		}

		const observer = new ResizeObserver(updateWidth);
		observer.observe(element);
		return () => observer.disconnect();
	}, [ref]);

	return width;
};

// @desc DataGrid footer pagination that adapts to every task pane width
const CustomPagination = ({ page = 0, pageSize, rowCount = 0, onPageChange, currentOfficeTheme }) => {
	const containerRef = React.useRef(null);
	const availableWidth = useElementWidth(containerRef);

	const pageCount = pageSize > 0 ? Math.ceil(rowCount / pageSize) : 0;
	const from = rowCount === 0 ? 0 : page * pageSize + 1;
	const to = Math.min(rowCount, (page + 1) * pageSize);
	const label = `${from}–${to} of ${rowCount}`;

	const layout = getPaginationLayout(availableWidth, pageCount, label);

	return (
		<div
			ref={containerRef}
			style={{
				flex: '1 1 0',
				minWidth: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end',
				gap: LABEL_GAP,
				overflow: 'hidden',
				whiteSpace: 'nowrap',
			}}
		>
			{layout.showLabel && (
				<span
					style={{
						fontSize: '12px',
						fontFamily: baseFontFamilyStyle,
						color: currentOfficeTheme.bodyForegroundColor,
					}}
				>
					{label}
				</span>
			)}
			<MuiPagination
				color="primary"
				aria-label="Pagination"
				size="small"
				siblingCount={layout.siblingCount}
				boundaryCount={layout.boundaryCount}
				showFirstButton
				showLastButton
				count={pageCount}
				page={page + 1}
				onChange={(event, newPage) => onPageChange(newPage - 1)}
				renderItem={item =>
					layout.currentPageOnly &&
					(item.type === 'start-ellipsis' ||
						item.type === 'end-ellipsis' ||
						(item.type === 'page' && !item.selected)) ? null : (
						<PaginationItem {...item} />
					)
				}
				sx={{
					flexShrink: 0,
					'& .MuiPagination-ul': {
						flexWrap: 'nowrap',
					},
					'.MuiPaginationItem-root': {
						color: currentOfficeTheme.bodyForegroundColor,
						minWidth: ITEM_MIN_WIDTH,
						height: ITEM_MIN_WIDTH,
						margin: `0 ${ITEM_MARGIN_X / 2}px`,
						padding: `0 ${ITEM_PADDING_X / 2}px`,
					},
					button: { color: currentOfficeTheme.bodyForegroundColor, fontFamily: baseFontFamilyStyle },
				}}
			/>
		</div>
	);
};

export default React.memo(CustomPagination);
